import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { getAdminPayments, postRefundPayment } from 'utils/api/admin';

const TYPE_LABEL = {
  subscription: '구독',
  upgrade_charge: '업그레이드',
  refund: '환불',
};
const STATUS_LABEL = { paid: '결제완료', cancelled: '취소', failed: '실패' };

const Payments = () => {
  const [payments, setPayments] = useState([]);
  const [total, setTotal] = useState(0);
  const [type, setType] = useState('');
  const [page, setPage] = useState(1);
  const size = 20;
  const [refundTarget, setRefundTarget] = useState(null);
  const [refundForm, setRefundForm] = useState({
    reason: '',
    cancelAmount: '',
    downgradePlan: true,
  });
  const [refunding, setRefunding] = useState(false);

  useEffect(() => {
    fetchPayments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, type]);

  const fetchPayments = async () => {
    try {
      const res = await getAdminPayments({ type, page, size });
      setPayments(res.results.payments);
      setTotal(res.results.total);
    } catch (err) {
      console.error(err);
    }
  };

  const openRefund = (payment) => {
    setRefundTarget(payment);
    setRefundForm({
      reason: '',
      cancelAmount: '',
      downgradePlan:
        payment.type === 'subscription' || payment.type === 'upgrade_charge',
    });
  };

  const closeRefund = () => {
    setRefundTarget(null);
    setRefunding(false);
  };

  const handleRefund = async (e) => {
    e.preventDefault();
    if (!refundTarget) return;
    if (!window.confirm('해당 결제를 환불 처리하시겠습니까?')) return;
    setRefunding(true);
    try {
      const params = {
        reason: refundForm.reason || undefined,
        cancelAmount: refundForm.cancelAmount
          ? Number(refundForm.cancelAmount)
          : undefined,
        downgradePlan: refundForm.downgradePlan,
      };
      await postRefundPayment(refundTarget._id, params);
      closeRefund();
      fetchPayments();
    } catch (err) {
      alert(err?.response?.data?.message || '환불 처리에 실패했습니다.');
      setRefunding(false);
    }
  };

  const totalPages = Math.ceil(total / size);
  const formatDate = (d) => (d ? new Date(d).toLocaleString('ko-KR') : '-');
  const canRefund = (p) =>
    p.status === 'paid' && p.type !== 'refund' && !!p.tid;

  return (
    <Container>
      <Title>결제 내역</Title>
      <FilterRow>
        <Select
          value={type}
          onChange={(e) => {
            setType(e.target.value);
            setPage(1);
          }}
        >
          <option value="">전체 타입</option>
          <option value="subscription">구독</option>
          <option value="upgrade_charge">업그레이드</option>
          <option value="refund">환불</option>
        </Select>
        <Info>총 {total}건</Info>
      </FilterRow>
      <Table>
        <thead>
          <tr>
            <Th>유저</Th>
            <Th>결제일</Th>
            <Th>타입</Th>
            <Th>금액</Th>
            <Th>상태</Th>
            <Th>플랜</Th>
            <Th>TID</Th>
            <Th>관리</Th>
          </tr>
        </thead>
        <tbody>
          {payments.map((p) => (
            <tr key={p._id}>
              <Td>
                <div>{p.userName || '-'}</div>
                <div style={{ fontSize: '12px', color: '#8f9bba' }}>
                  {p.userEmail || ''}
                </div>
              </Td>
              <Td>{formatDate(p.createdAt)}</Td>
              <Td>{TYPE_LABEL[p.type] || p.type}</Td>
              <Td>{p.amount?.toLocaleString()}원</Td>
              <Td>
                <StatusBadge status={p.status}>
                  {STATUS_LABEL[p.status] || p.status}
                </StatusBadge>
              </Td>
              <Td>{p.plan || '-'}</Td>
              <Td style={{ fontSize: '12px', color: '#8f9bba' }}>{p.tid}</Td>
              <Td>
                {canRefund(p) && (
                  <RefundBtn onClick={() => openRefund(p)}>환불</RefundBtn>
                )}
              </Td>
            </tr>
          ))}
        </tbody>
      </Table>
      {totalPages > 1 && (
        <Pagination>
          <PageBtn disabled={page <= 1} onClick={() => setPage(page - 1)}>
            이전
          </PageBtn>
          <PageInfo>
            {page} / {totalPages}
          </PageInfo>
          <PageBtn
            disabled={page >= totalPages}
            onClick={() => setPage(page + 1)}
          >
            다음
          </PageBtn>
        </Pagination>
      )}

      {refundTarget && (
        <Overlay onClick={closeRefund}>
          <ModalBox onClick={(e) => e.stopPropagation()}>
            <ModalTitle>결제 환불</ModalTitle>
            <ModalInfo>
              {refundTarget.userEmail} · {TYPE_LABEL[refundTarget.type]} ·{' '}
              {refundTarget.amount?.toLocaleString()}원
            </ModalInfo>
            <Form onSubmit={handleRefund}>
              <Label>환불 사유</Label>
              <Input
                placeholder="예: 고객 요청"
                value={refundForm.reason}
                onChange={(e) =>
                  setRefundForm({ ...refundForm, reason: e.target.value })
                }
              />
              <Label>부분 환불 금액 (원) — 비우면 전체 환불</Label>
              <Input
                type="number"
                min="0"
                max={refundTarget.amount}
                placeholder={String(refundTarget.amount || '')}
                value={refundForm.cancelAmount}
                onChange={(e) =>
                  setRefundForm({
                    ...refundForm,
                    cancelAmount: e.target.value,
                  })
                }
              />
              {(refundTarget.type === 'subscription' ||
                refundTarget.type === 'upgrade_charge') && (
                <CheckboxRow>
                  <input
                    type="checkbox"
                    id="downgrade"
                    checked={refundForm.downgradePlan}
                    onChange={(e) =>
                      setRefundForm({
                        ...refundForm,
                        downgradePlan: e.target.checked,
                      })
                    }
                  />
                  <label htmlFor="downgrade">
                    구독 해지 + 유저 FREE로 다운그레이드
                  </label>
                </CheckboxRow>
              )}
              <ModalActions>
                <CancelBtn type="button" onClick={closeRefund}>
                  취소
                </CancelBtn>
                <DangerBtn type="submit" disabled={refunding}>
                  {refunding ? '처리 중...' : '환불 처리'}
                </DangerBtn>
              </ModalActions>
            </Form>
          </ModalBox>
        </Overlay>
      )}
    </Container>
  );
};

const Container = styled.div``;
const Title = styled.h1`
  font-size: 22px;
  font-weight: 700;
  color: #232d42;
  margin-bottom: 24px;
`;
const FilterRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 16px;
`;
const Select = styled.select`
  padding: 8px 12px;
  border: 1px solid #e4e4e4;
  border-radius: 8px;
  font-size: 14px;
  outline: none;
`;
const Info = styled.div`
  font-size: 13px;
  color: #8f9bba;
`;
const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: #fff;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
`;
const Th = styled.th`
  text-align: left;
  padding: 12px 16px;
  font-size: 13px;
  font-weight: 600;
  color: #8f9bba;
  border-bottom: 1px solid #e4e4e4;
  background: #fafafa;
`;
const Td = styled.td`
  padding: 12px 16px;
  font-size: 14px;
  color: #232d42;
  border-bottom: 1px solid #f0f0f0;
`;
const StatusBadge = styled.span`
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  background: ${({ status }) =>
    status === 'paid'
      ? '#e6ffed'
      : status === 'cancelled'
      ? '#fff5f5'
      : '#f0f0f0'};
  color: ${({ status }) =>
    status === 'paid'
      ? '#38a169'
      : status === 'cancelled'
      ? '#e53e3e'
      : '#8f9bba'};
`;
const Pagination = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  margin-top: 20px;
`;
const PageBtn = styled.button`
  padding: 6px 16px;
  border: 1px solid #e4e4e4;
  border-radius: 8px;
  background: #fff;
  font-size: 13px;
  cursor: pointer;
  &:disabled {
    opacity: 0.4;
    cursor: default;
  }
`;
const PageInfo = styled.span`
  font-size: 13px;
  color: #8f9bba;
`;
const RefundBtn = styled.button`
  padding: 4px 12px;
  border-radius: 6px;
  border: 1px solid #e53e3e;
  background: #fff;
  color: #e53e3e;
  font-size: 12px;
  cursor: pointer;
  &:hover {
    background: #e53e3e;
    color: #fff;
  }
`;
const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;
const ModalBox = styled.div`
  background: #fff;
  border-radius: 16px;
  padding: 32px;
  width: 440px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
`;
const ModalTitle = styled.h2`
  font-size: 18px;
  font-weight: 700;
  color: #232d42;
  margin: 0 0 8px 0;
`;
const ModalInfo = styled.div`
  font-size: 13px;
  color: #8f9bba;
  margin-bottom: 20px;
`;
const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;
const Label = styled.label`
  font-size: 13px;
  font-weight: 600;
  color: #8f9bba;
  margin-top: 4px;
`;
const Input = styled.input`
  padding: 10px 12px;
  border: 1px solid #e4e4e4;
  border-radius: 8px;
  font-size: 14px;
  outline: none;
  &:focus {
    border-color: #644ded;
  }
`;
const CheckboxRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 12px;
  font-size: 13px;
  color: #232d42;
`;
const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 20px;
`;
const CancelBtn = styled.button`
  padding: 10px 24px;
  border: 1px solid #e4e4e4;
  border-radius: 8px;
  background: #fff;
  font-size: 14px;
  cursor: pointer;
`;
const DangerBtn = styled.button`
  padding: 10px 24px;
  border: none;
  border-radius: 8px;
  background: #e53e3e;
  color: #fff;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  &:hover {
    background: #c53030;
  }
  &:disabled {
    opacity: 0.6;
    cursor: default;
  }
`;

export default Payments;
